import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import toast from 'react-hot-toast';
import { Loader2, Search, Check, X, Calendar, DollarSign, Activity } from 'lucide-react';

const Subscriptions = () => {
  const [loading, setLoading] = useState(true);
  const [subscriptions, setSubscriptions] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [suggestionSort, setSuggestionSort] = useState('amount_desc');

  const fetchSubscriptions = useCallback(async () => {
    try {
      const res = await api.get('/plaid/analyze-history');
      setSubscriptions(res.data.suggestions || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSubscriptions();
  }, [fetchSubscriptions]);

  const handleIgnore = async (sub) => {
    setProcessingId(sub.merchant);
    try {
      await api.post('/plaid/ignore-subscription', { merchant_pattern: sub.merchant });
      toast.success('Subscription ignored');
      setSubscriptions(prev => prev.filter(s => s.merchant !== sub.merchant));
    } catch (err) {
      console.error(err);
      toast.error('Failed to ignore subscription');
    } finally {
      setProcessingId(null);
    }
  };

  const handleCreateBucket = async (sub) => {
    setProcessingId(sub.merchant);
    try {
      await api.post('/plaid/bucket-subscription', {
        name: sub.merchant,
        amount: sub.averageAmount,
        due_day: sub.estimatedDueDay || 1,
        merchant_pattern: sub.merchant
      });
      toast.success('Bucket created!');
      setSubscriptions(prev => prev.filter(s => s.merchant !== sub.merchant));
      window.dispatchEvent(new Event('thread_settings_updated'));
    } catch (err) {
      console.error(err);
      toast.error('Failed to create bucket');
    } finally {
      setProcessingId(null);
    }
  };

  return (
    <>
      <div className="container-main" style={{ animation: 'fade-in 0.4s ease-out' }}>
        <header style={{ marginBottom: '2rem', display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
          <div>
            <h1 style={{ margin: '0 0 0.5rem 0', fontSize: '2.5rem', background: 'linear-gradient(45deg, var(--primary), #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Subscription Tracker
            </h1>
            <p className="text-muted" style={{ fontSize: '1.1rem', margin: 0 }}>
              We've scanned your bank history to identify recurring charges. Turn them into Buckets to automate them, or ignore them to clean up this list.
            </p>
          </div>
          <select 
            className="input-base" 
            value={suggestionSort} 
            onChange={(e) => setSuggestionSort(e.target.value)}
            style={{ padding: '0.6rem 1rem', fontSize: '1rem', width: 'auto', background: 'var(--surface)', border: '1px solid var(--surface-border)', borderRadius: 'var(--radius-md)', color: 'var(--text-main)' }}
          >
            <option value="amount_desc">Sort: Amount (Highest)</option>
            <option value="amount_asc">Sort: Amount (Lowest)</option>
            <option value="name_asc">Sort: Name (A-Z)</option>
            <option value="name_desc">Sort: Name (Z-A)</option>
            <option value="due_date_asc">Sort: Due Date (Earliest)</option>
            <option value="due_date_desc">Sort: Due Date (Latest)</option>
          </select>
        </header>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '4rem', color: 'var(--text-muted)' }}>
            <Loader2 size={48} style={{ animation: 'spin 2s linear infinite', marginBottom: '1rem', color: 'var(--primary)' }} />
            <p>Scanning history for subscriptions...</p>
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="glass" style={{ textAlign: 'center', padding: '4rem 2rem', borderRadius: 'var(--radius-lg)' }}>
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(59, 130, 246, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.5rem auto' }}>
              <Check size={40} color="var(--primary)" />
            </div>
            <h3 style={{ margin: '0 0 0.5rem 0' }}>All Caught Up!</h3>
            <p className="text-muted" style={{ maxWidth: '400px', margin: '0 auto' }}>
              We couldn't find any more recurring subscriptions in your history. You've either bucketed or ignored them all!
            </p>
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '1.5rem' }}>
            {[...subscriptions].sort((a, b) => {
              if (suggestionSort === 'amount_desc') return b.averageAmount - a.averageAmount;
              if (suggestionSort === 'amount_asc') return a.averageAmount - b.averageAmount;
              if (suggestionSort === 'name_asc') return a.merchant.localeCompare(b.merchant);
              if (suggestionSort === 'name_desc') return b.merchant.localeCompare(a.merchant);
              if (suggestionSort === 'due_date_asc') return (a.estimatedDueDay || 1) - (b.estimatedDueDay || 1);
              if (suggestionSort === 'due_date_desc') return (b.estimatedDueDay || 1) - (a.estimatedDueDay || 1);
              return 0;
            }).map((sub, idx) => (
              <div 
                key={sub.merchant} 
                className="glass" 
                style={{ 
                  padding: '1.5rem', 
                  borderRadius: 'var(--radius-md)', 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: '1rem',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: `fade-in 0.4s ease-out ${idx * 0.05}s both`,
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  opacity: processingId === sub.merchant ? 0.5 : 1,
                  pointerEvents: processingId === sub.merchant ? 'none' : 'auto',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
              >
                {/* Decorative glow */}
                <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '100px', height: '100px', background: 'var(--primary)', filter: 'blur(50px)', opacity: 0.15, borderRadius: '50%' }}></div>
                
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <h3 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 'bold', textTransform: 'capitalize' }}>
                    {sub.merchant.toLowerCase()}
                  </h3>
                  <div style={{ background: 'rgba(59, 130, 246, 0.1)', color: 'var(--primary)', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                    Spotted {sub.occurrences}x
                  </div>
                </div>

                <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.25rem' }}>
                  <span style={{ fontSize: '2rem', fontWeight: 'bold' }}>${sub.averageAmount.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  <span className="text-muted" style={{ fontSize: '0.9rem' }}>/mo</span>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                  <Calendar size={16} /> Est. Due: {sub.estimatedDueDay}{[1, 21, 31].includes(sub.estimatedDueDay) ? 'st' : [2, 22].includes(sub.estimatedDueDay) ? 'nd' : [3, 23].includes(sub.estimatedDueDay) ? 'rd' : 'th'}
                </div>

                <div style={{ display: 'flex', gap: '0.75rem', marginTop: 'auto', paddingTop: '1rem' }}>
                  <button 
                    className="btn btn-primary" 
                    onClick={() => handleCreateBucket(sub)}
                    style={{ flex: 2, padding: '0.6rem', display: 'flex', justifyContent: 'center', gap: '0.5rem' }}
                  >
                    <Check size={18} /> Create Bucket
                  </button>
                  <button 
                    className="btn btn-glass" 
                    onClick={() => handleIgnore(sub)}
                    style={{ flex: 1, padding: '0.6rem', display: 'flex', justifyContent: 'center', color: 'var(--text-muted)' }}
                    title="Ignore this vendor"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default Subscriptions;
